import { useEffect, useMemo, useState } from "react";
import { api, formatErr } from "../lib/api";
import DashboardShell from "../components/DashboardShell";
import { RiyalSymbol, SAR } from "../components/RiyalIcon";
import { Plus, Pencil, X, FolderPlus, Trash2, ArrowUpDown } from "lucide-react";
import { toast } from "sonner";
import ImageUpload, { absoluteUrl } from "../components/ImageUpload";
import OptionGroupsEditor from "../components/OptionGroupsEditor";
import MenuOrderEditor from "../components/MenuOrderEditor";
import { useAuth } from "../context/AuthContext";

function useMenuPerms() {
  const { user } = useAuth();
  const owner = user?.role === "OWNER";
  const p = user?.permissions || {};
  const edit = owner || !!p.products_edit;
  return {
    add: owner || !!p.products_add,
    edit,
    del: owner || !!p.products_delete,
    price: owner || (edit && !!p.prices_edit),
    addons: owner || (edit && !!p.addons_edit),
  };
}

export default function MenuManagement() {
  const can = useMenuPerms();
  const [cats, setCats] = useState([]);
  const [prods, setProds] = useState([]);
  const [editing, setEditing] = useState(null);
  const [adding, setAdding] = useState(false);
  const [addingCat, setAddingCat] = useState(false);
  const [loading, setLoading] = useState(true);
  const [ordering, setOrdering] = useState(false);

  const load = async () => {
    try {
      const [c, p] = await Promise.all([api.get("/categories"), api.get("/products")]);
      setCats(c.data); setProds(p.data);
    } catch (e) { toast.error(formatErr(e)); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const grouped = cats.map(c => ({ ...c, items: prods.filter(p => p.category_id === c.id) }));
  const toggle = async (p) => {
    try { await api.patch(`/products/${p.id}/availability?available=${!p.available}`); load(); }
    catch (e) { toast.error(formatErr(e)); }
  };
  const isEmpty = !loading && cats.length === 0 && prods.length === 0;

  return (
    <DashboardShell title="المنيو">
      {ordering ? (
        <MenuOrderEditor
          categories={cats}
          products={prods}
          onReload={(c, p) => { setCats(c); setProds(p); }}
          onExit={() => { setOrdering(false); load(); }}
        />
      ) : isEmpty ? (
        <div className="mt-8 text-center bg-white rounded-3xl border border-orange-100 p-8 card-shadow" data-testid="empty-menu">
          <div className="text-6xl mb-3">🍽️</div>
          <div className="font-extrabold text-xl">منيو محلك يبدأ من هنا</div>
          <div className="text-sm text-gray-600 mt-1 mb-5">أضف أصنافك ليتمكن عملاؤك من الطلب</div>
          {can.add && (
            <button onClick={() => setAddingCat(true)} className="pill brand-grad text-white px-5 py-3 font-bold inline-flex items-center gap-1" data-testid="add-first-cat">
              <FolderPlus size={16}/> إضافة أول قسم
            </button>
          )}
        </div>
      ) : (
        <>
          {can.add && (
            <div className="flex gap-2 mb-4">
              <button
                onClick={() => {
                  if (!loading) cats.length ? setAdding(true) : toast.error("أضف قسماً أولاً");
                }}
                className="flex-1 pill brand-grad text-white font-bold py-3 tap-target inline-flex items-center justify-center gap-2"
                data-testid="add-product-btn"
              >
                <Plus size={18}/> إضافة صنف
              </button>
              <button onClick={() => setAddingCat(true)} className="pill bg-white border border-orange-200 text-orange-700 px-4 font-bold tap-target inline-flex items-center gap-2" data-testid="add-category-btn">
                <FolderPlus size={18}/> قسم
              </button>
            </div>
          )}

          {can.edit && (
            <button onClick={() => setOrdering(true)} data-testid="edit-order-btn" disabled={loading}
              className="w-full mb-4 pill bg-white border border-orange-200 text-orange-700 py-2.5 font-bold tap-target inline-flex items-center justify-center gap-2">
              <ArrowUpDown size={16}/> تعديل الترتيب
            </button>
          )}

          {grouped.map(c => (
            <section key={c.id} className="mb-5">
              <div className="flex items-center justify-between mb-2">
                <h2 className="font-extrabold text-lg">{c.name}</h2>
                {can.del && <button onClick={() => { if (window.confirm("حذف القسم؟")) api.delete(`/categories/${c.id}`).then(load).catch(e=>toast.error(formatErr(e))); }} className="text-red-500" data-testid={`del-cat-${c.id}`}><Trash2 size={16}/></button>}
              </div>
              <div className="space-y-2">
                {c.items.length === 0 && <div className="text-sm text-gray-500 bg-white rounded-2xl border border-dashed border-orange-200 p-3 text-center">لا توجد أصناف — اضغط «إضافة صنف»</div>}
                {c.items.map(p => (
                  <div key={p.id} className="bg-white rounded-2xl border border-orange-100 p-3 flex items-center gap-3" data-testid={`menu-item-${p.id}`}>
                    <div className="w-16 h-16 rounded-2xl food-img overflow-hidden shrink-0">
                      {p.image_url ? <img src={absoluteUrl(p.image_url)} alt="" className="w-full h-full object-cover" /> : <div className="w-full h-full grid place-items-center text-2xl">🍽️</div>}
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="font-bold truncate">{p.name}</div>
                      <div className="text-orange-600 font-bold text-sm"><SAR>{p.price}</SAR></div>
                      {(p.option_groups || []).length > 0 && <div className="text-xs text-gray-500 mt-0.5">{p.option_groups.length} مجموعة خيارات</div>}
                    </div>
                    {can.edit && (
                      <button onClick={() => toggle(p)} data-testid={`toggle-${p.id}`}
                        className={`pill text-xs font-bold px-3 py-1.5 ${p.available ? "bg-green-100 text-green-800" : "bg-gray-100 text-gray-500"}`}>
                        {p.available ? "🟢" : "⚪"}
                      </button>
                    )}
                    {(can.edit || can.del) && <button onClick={() => setEditing(p)} data-testid={`edit-${p.id}`} className="w-9 h-9 rounded-xl bg-orange-50 grid place-items-center text-orange-700">{can.edit ? <Pencil size={16}/> : <Trash2 size={16}/>}</button>}
                  </div>
                ))}
              </div>
            </section>
          ))}
        </>
      )}

      {(editing || adding) && (
        <ProductDialog initial={editing} categories={cats} can={can}
          onClose={() => { setEditing(null); setAdding(false); }}
          onSaved={() => { setEditing(null); setAdding(false); load(); }} />
      )}
      {addingCat && (
        <CategoryDialog onClose={() => setAddingCat(false)} onSaved={() => { setAddingCat(false); load(); }} />
      )}
    </DashboardShell>
  );
}

function CategoryDialog({ onClose, onSaved }) {
  const [name, setName] = useState("");
  const [busy, setBusy] = useState(false);
  const save = async () => {
    if (!name.trim()) return toast.error("أدخل اسم القسم");
    setBusy(true);
    try { await api.post("/categories", { name }); onSaved(); }
    catch (e) { toast.error(formatErr(e)); }
    finally { setBusy(false); }
  };
  return (
    <div className="fixed inset-0 z-40 bg-black/50 grid place-items-end sm:place-items-center p-4" onClick={onClose}>
      <div onClick={e => e.stopPropagation()} className="w-full max-w-md bg-white rounded-3xl p-5 rise-in">
        <div className="flex items-center justify-between mb-3">
          <div className="font-extrabold text-lg">إضافة قسم</div>
          <button onClick={onClose} className="w-9 h-9 rounded-xl bg-gray-100 grid place-items-center"><X size={18}/></button>
        </div>
        <label className="block">
          <span className="text-xs font-bold text-gray-600">اسم القسم</span>
          <input value={name} onChange={e => setName(e.target.value)} placeholder="مثال: الشاورما" data-testid="cat-name"
            className="mt-1 w-full h-12 rounded-2xl bg-gray-50 border border-gray-200 px-4 outline-none focus:border-orange-500" />
        </label>
        <button disabled={busy} onClick={save} className="mt-4 w-full pill brand-grad text-white font-bold py-3 tap-target disabled:opacity-60" data-testid="cat-save">{busy ? "جاري…" : "إضافة"}</button>
      </div>
    </div>
  );
}

function ProductDialog({ initial, categories, can, onClose, onSaved }) {
  // Editing an existing product needs products_edit; price/add-on fields follow their own flags.
  const canSave = initial ? can.edit : can.add;
  const canPrice = !initial || can.price;
  const canAddons = !initial || can.addons;
  const initialForm = useMemo(() => ({
    name: initial?.name || "",
    description: initial?.description || "",
    price: initial?.price ?? "",
    image_url: initial?.image_url || "",
    category_id: initial?.category_id || categories[0]?.id || "",
    available: initial?.available ?? true,
    option_groups: initial?.option_groups || [],
  }), [initial, categories]);
  const [f, setF] = useState(() => cloneProductForm(initialForm));
  const [busy, setBusy] = useState(false);
  const [errors, setErrors] = useState({});
  const [showDiscardConfirm, setShowDiscardConfirm] = useState(false);
  const isDirty = !areProductFormsEqual(f, initialForm);

  const requestClose = () => {
    if (busy) return;
    if (isDirty) {
      setShowDiscardConfirm(true);
      return;
    }
    onClose();
  };

  const discardAndClose = () => {
    setShowDiscardConfirm(false);
    onClose();
  };
  const clearError = (field) => {
    setErrors((current) => {
      const keys = Object.keys(current).filter((key) => (
        key === field || key.startsWith(`${field}.`)
      ));
      if (keys.length === 0) {
        return current;
      }
      const next = { ...current };
      keys.forEach((key) => delete next[key]);
      return next;
    });
  };
  const save = async () => {
    const validationErrors = validateProductForm(f);
    if (Object.keys(validationErrors).length > 0) {
      setErrors(validationErrors);
      showValidationNotification(validationErrors);
      return;
    }
    setErrors({});
    setBusy(true);
    try {
      const payload = { ...f, price: Number(f.price) };
      if (initial) await api.patch(`/products/${initial.id}`, payload);
      else await api.post("/products", payload);
      toast.success("تم الحفظ");
      onSaved();
    } catch (e) {
      const backendErrors = mapBackendProductErrors(e, f);
      if (Object.keys(backendErrors).length > 0) {
        setErrors(backendErrors);
        showValidationNotification(backendErrors);
      } else {
        toast.error(formatErr(e));
      }
    }
    finally { setBusy(false); }
  };
  const del = async () => {
    if (!initial) return;
    if (!window.confirm("حذف هذا الصنف؟")) return;
    try { await api.delete(`/products/${initial.id}`); onSaved(); }
    catch (e) { toast.error(formatErr(e)); }
  };

  return (
    <div
      className="fixed inset-0 z-40 grid place-items-end bg-black/50 p-4 sm:place-items-center"
      data-testid="product-dialog-backdrop"
      onClick={requestClose}
    >
      <div
        className="w-full max-w-md max-h-[92vh] overflow-y-auto rounded-3xl bg-white p-5 rise-in"
        data-testid="product-dialog"
        onClick={e => e.stopPropagation()}
      >
        <div className="flex items-center justify-between mb-3">
          <div className="font-extrabold text-lg">{initial ? "تعديل صنف" : "إضافة صنف"}</div>
          <button
            type="button"
            onClick={requestClose}
            className="grid h-9 w-9 place-items-center rounded-xl bg-gray-100"
            data-testid="product-dialog-close"
            aria-label="إغلاق نموذج المنتج"
          >
            <X size={18}/>
          </button>
        </div>
        <div className="space-y-3">
          <Input
            label="اسم الصنف"
            value={f.name}
            onChange={v => {
              setF({...f, name: v});
              clearError("name");
            }}
            testid="pd-name"
            error={errors.name}
          />
          <label className="block">
            <span className="text-xs font-bold text-gray-600">وصف المنتج (اختياري)</span>
            <textarea
              value={f.description}
              onChange={e => setF({...f, description: e.target.value})}
              placeholder="اكتب وصفًا مختصرًا للصنف"
              data-testid="pd-description"
              className={
                "mt-1 min-h-20 w-full resize-none rounded-2xl border border-gray-200 bg-gray-50 "
                + "px-4 py-3 outline-none focus:border-orange-500 focus:ring-2 focus:ring-orange-100"
              }
            />
          </label>
          <Input
            label={
              <span className="inline-flex items-center gap-1">
                السعر (<RiyalSymbol />)
              </span>
            }
            value={f.price}
            onChange={v => {
              setF({...f, price: v});
              clearError("price");
            }}
            type="number"
            min="0"
            testid="pd-price"
            error={errors.price}
            disabled={!canPrice}
          />
          <ImageUpload
            value={f.image_url}
            onChange={(url) => setF({...f, image_url: url || ""})}
            label="صورة الصنف (اختياري)"
            testid="pd-image"
          />
          <div>
            <div className="text-xs font-bold text-gray-600">القسم</div>
            <select
              value={f.category_id}
              onChange={e => {
                setF({...f, category_id: e.target.value});
                clearError("category_id");
              }}
              data-testid="pd-category"
              className={
                "mt-1 w-full h-12 rounded-2xl bg-gray-50 border px-3 outline-none "
                + (errors.category_id
                  ? "border-red-500 focus:border-red-500"
                  : "border-gray-200 focus:border-orange-500")
              }
            >
              {categories.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
            </select>
            {errors.category_id && (
              <p className="mt-1 text-xs text-red-600" data-testid="pd-category-error">
                {errors.category_id}
              </p>
            )}
          </div>
          <label className="flex items-center gap-2 mt-1">
            <input
              type="checkbox"
              checked={f.available}
              onChange={e => setF({...f, available: e.target.checked})}
              data-testid="pd-available"
              className="w-5 h-5 accent-orange-600"
            />
            <span className="text-sm">متوفر</span>
          </label>

          <div className="pt-3 border-t border-orange-100">
            {canAddons ? (
              <OptionGroupsEditor
                value={f.option_groups}
                onChange={(groups) => setF({...f, option_groups: groups})}
                errors={errors}
                onFieldChange={clearError}
              />
            ) : (
              <div className="text-xs text-gray-500" data-testid="pd-addons-locked">
                مجموعات الخيارات: {(f.option_groups || []).length} — ليس لديك صلاحية تعديل الإضافات
              </div>
            )}
          </div>
        </div>
        <div className="mt-4 flex gap-2">
          {canSave && <button disabled={busy} onClick={save} className="flex-1 pill brand-grad text-white font-bold py-3 tap-target disabled:opacity-60" data-testid="pd-save">{busy ? "جاري…" : "حفظ"}</button>}
          {initial && can.del && <button onClick={del} className="pill bg-red-50 text-red-700 border border-red-100 px-4 py-3 font-bold" data-testid="pd-delete">حذف</button>}
        </div>
      </div>
      {showDiscardConfirm && (
        <div
          className="fixed inset-0 z-50 grid place-items-center bg-black/60 p-4"
          data-testid="discard-changes-backdrop"
          onClick={e => e.stopPropagation()}
        >
          <div
            className="w-full max-w-sm rounded-3xl bg-white p-6 text-right shadow-2xl rise-in"
            role="alertdialog"
            aria-modal="true"
            aria-labelledby="discard-changes-title"
            aria-describedby="discard-changes-description"
            data-testid="discard-changes-modal"
          >
            <h2
              id="discard-changes-title"
              className="text-lg font-extrabold text-gray-900"
              data-testid="discard-changes-title"
            >
              لديك تعديلات غير محفوظة، هل تريد حفظها؟
            </h2>
            <p
              id="discard-changes-description"
              className="mt-2 text-sm leading-6 text-gray-600"
              data-testid="discard-changes-description"
            >
              اختر «نعم» للبقاء في النافذة ثم استخدم زر الحفظ، أو «لا» للخروج دون حفظ.
            </p>
            <div className="mt-6 flex flex-col gap-2 sm:flex-row sm:justify-end">
              <button
                type="button"
                onClick={() => setShowDiscardConfirm(false)}
                className="pill border border-orange-200 bg-white px-4 py-3 font-bold text-orange-700"
                data-testid="product-unsaved-changes-yes"
              >
                نعم
              </button>
              <button
                type="button"
                onClick={discardAndClose}
                className="pill bg-red-600 px-4 py-3 font-bold text-white hover:bg-red-700"
                data-testid="product-unsaved-changes-no"
              >
                لا
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function cloneProductForm(form) {
  return JSON.parse(JSON.stringify(form));
}

function areProductFormsEqual(current, baseline) {
  return JSON.stringify(current) === JSON.stringify(baseline);
}

function Input({
  label,
  value,
  onChange,
  type = "text",
  min,
  testid,
  error,
  disabled = false,
}) {
  return (
    <label className="block">
      <span className="text-xs font-bold text-gray-600">{label}</span>
      <input
        type={type}
        min={min}
        value={value}
        disabled={disabled}
        onChange={e => onChange(e.target.value)}
        data-testid={testid}
        className={
          "mt-1 w-full h-12 rounded-2xl bg-gray-50 border px-4 outline-none focus:ring-2 disabled:opacity-60 "
          + (error
            ? "border-red-500 focus:border-red-500 focus:ring-red-100"
            : "border-gray-200 focus:border-orange-500 focus:ring-orange-100")
        }
      />
      {error && (
        <p className="mt-1 text-xs text-red-600" data-testid={`${testid}-error`}>
          {error}
        </p>
      )}
    </label>
  );
}

function validateProductForm(form) {
  const errors = {};
  if (!form.name.trim()) errors.name = "اسم الصنف مطلوب";
  if (form.price === "") errors.price = "السعر مطلوب";
  else if (Number(form.price) < 0) errors.price = "السعر لا يمكن أن يكون سالباً";
  if (!form.category_id) errors.category_id = "اختر القسم";

  (form.option_groups || []).forEach((group, groupIndex) => {
    const prefix = `option-groups.${groupIndex}`;
    const minimum = Number(group.min) || 0;
    const maximum = group.max === null ? null : Number(group.max);
    if (!group.name?.trim()) {
      errors[`${prefix}.name`] = "الرجاء إدخال اسم المجموعة";
    }
    if (minimum < 0) errors[`${prefix}.min`] = "الحد الأدنى لا يمكن أن يكون سالباً";
    if (maximum !== null && maximum <= 0) {
      errors[`${prefix}.max`] = "الحد الأقصى للاختيار يجب أن يكون أكبر من 0";
    }
    if (maximum !== null && minimum > maximum) {
      errors[`${prefix}.min`] = "الحد الأدنى لا يمكن أن يتجاوز الحد الأقصى";
      errors[`${prefix}.max`] = "الحد الأدنى لا يمكن أن يتجاوز الحد الأقصى";
    }
    if (group.required && minimum < 1) {
      errors[`${prefix}.min`] = "المجموعة الإلزامية تحتاج حداً أدنى واحداً على الأقل";
    }
    if (minimum > 0 && (group.options || []).length === 0) {
      errors[`${prefix}.options`] = "أضف خياراً واحداً على الأقل لهذه المجموعة";
    }
    (group.options || []).forEach((option, optionIndex) => {
      if (!option.name?.trim()) {
        const optionNameKey = `${prefix}.options.${optionIndex}.name`;
        errors[optionNameKey] = "الرجاء إدخال اسم الخيار أو إزالته";
      }
      if (Number(option.price_delta) < 0) {
        const optionPriceKey = `${prefix}.options.${optionIndex}.price_delta`;
        errors[optionPriceKey] = "سعر الخيار لا يمكن أن يكون سالباً";
      }
    });
  });
  return errors;
}

function showValidationNotification(errors) {
  const messages = Object.values(errors);
  if (messages.length === 1) {
    toast.error(messages[0]);
    return;
  }
  toast.error(`يوجد ${messages.length} أخطاء، يرجى تصحيحها قبل الحفظ`);
}

function mapBackendProductErrors(error, form) {
  const detail = error?.response?.data?.detail;
  const errors = {};
  if (Array.isArray(detail)) {
    detail.forEach((item) => {
      const path = (item.loc || []).filter((part) => part !== "body");
      if (["name", "price", "category_id"].includes(path[0])) {
        errors[path[0]] = item.msg;
      }
      if (path[0] === "option_groups" && Number.isInteger(path[1])) {
        const prefix = `option-groups.${path[1]}`;
        if (["min", "max"].includes(path[2])) {
          errors[`${prefix}.${path[2]}`] = item.msg;
        }
        if (
          path[2] === "options"
          && Number.isInteger(path[3])
          && path[4] === "price_delta"
        ) {
          errors[`${prefix}.options.${path[3]}.price_delta`] = item.msg;
        }
      }
    });
  }
  if (typeof detail !== "string") return errors;

  (form.option_groups || []).forEach((group, groupIndex) => {
    const prefix = `option-groups.${groupIndex}`;
    const minimum = Number(group.min) || 0;
    const maximum = group.max === null ? null : Number(group.max);
    if (detail.includes("الحد الأقصى") && maximum !== null && maximum <= 0) {
      errors[`${prefix}.max`] = detail;
    }
    if (detail.includes("لا يمكن أن تكون سالبة") && minimum < 0) {
      errors[`${prefix}.min`] = detail;
    }
    if (detail.includes("لا يمكن أن يتجاوز") && maximum !== null && minimum > maximum) {
      errors[`${prefix}.min`] = detail;
      errors[`${prefix}.max`] = detail;
    }
    if (detail.includes("المجموعة الإلزامية") && group.required && minimum < 1) {
      errors[`${prefix}.min`] = detail;
    }
    if (
      detail.includes("تحتاج خياراً")
      && minimum > 0
      && (group.options || []).length === 0
    ) {
      errors[`${prefix}.options`] = detail;
    }
  });
  return errors;
}
